import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Dialog, Tabs } from '@ygtechnologies/rubus-ui';
import { faArrowsRotate, faFileLines } from '@fortawesome/pro-light-svg-icons';
import {
    AwsEnvironmentReport,
    AwsTailLogs,
    AwsReportMetrics,
    errorText,
    fetchAwsLogs,
    getAwsReport,
    getAwsReportMetrics,
    getSlowRequests,
    MetricRange,
    SlowRequest,
} from '@/services/systemHealth';
import { Stat } from './Stat';
import { diagnose } from './serviceDiagnosis';
import { formatCount, formatMs, formatPercent, orDash, timeAgo } from './format';
import shared from './SystemHealthPage.module.scss';
import theme from './ServiceReport.module.scss';

type ReportTab = 'overview' | 'slow' | 'monitoring' | 'logs' | 'events';
const reportTabs: { id: ReportTab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'slow', label: 'Slow requests' },
    { id: 'monitoring', label: 'Monitoring' },
    { id: 'logs', label: 'Logs' },
    { id: 'events', label: 'Events' },
];
const ranges: MetricRange[] = ['30m', '3h', '24h'];
const SLOW_MS = 1000;
const ERROR_LINE = /\b(error|exception|fail(ed|ure)?|fatal|crit(ical)?|unhandled)\b/i;
const clock = (d: Date) => d.toLocaleString('en-GB', { weekday: 'short', hour: '2-digit', minute: '2-digit' });

/** Opens when a service card is clicked, so a problem can be looked into without leaving the dashboard. */
const ServiceReport: React.FC<{ environmentName: string | null; now: number; onClose(): void }> = ({
    environmentName,
    now,
    onClose,
}) => {
    const [report, setReport] = useState<AwsEnvironmentReport | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [tab, setTab] = useState<ReportTab>('overview');

    // Only the newest request may land: a slow answer for the previous service mustn't replace this one.
    const latest = useRef(0);
    const load = useCallback(() => {
        if (!environmentName) return;
        const request = ++latest.current;
        setLoading(true);
        getAwsReport(environmentName)
            .then((r) => {
                if (request !== latest.current) return;
                setReport(r);
                setError(null);
            })
            .catch(async (e) => {
                const message = await errorText(e);
                if (request === latest.current) setError(message);
            })
            .finally(() => request === latest.current && setLoading(false));
    }, [environmentName]);

    useEffect(() => {
        setReport(null);
        setError(null);
        setTab('overview');
        load();
    }, [load]);

    const s = report?.service;

    return (
        <Dialog open={!!environmentName} onOpenChange={(open) => !open && onClose()}>
            <Dialog.Content contentWidth="lg">
                <Dialog.Header>
                    <span className={theme.title}>
                        {s && <span className={`${shared.dot} ${shared[s.health]}`} />}
                        {s?.name ?? environmentName}
                        {s && <span className={shared.muted}>{s.environmentName}</span>}
                    </span>
                </Dialog.Header>

                {error && <p className={theme.error}>{error}</p>}
                {!report && !error && <p className={shared.muted}>Loading report…</p>}

                {report && s && environmentName && (
                    <div className={theme.body}>
                        <div className={theme.meta}>
                            <span>
                                {s.healthStatus ?? s.health}
                                {s.status && s.status !== 'Ready' ? ` · ${s.status}` : ''}
                            </span>
                            <span>
                                {s.instancesOk ?? '—'}/{s.instancesTotal ?? '—'} instances healthy
                            </span>
                            <span>Updated {s.updatedAt ? timeAgo(s.updatedAt, now) : '—'}</span>
                            <Button size="xs" variant="tertiary" icon={faArrowsRotate} onClick={load} disabled={loading}>
                                Refresh
                            </Button>
                        </div>

                        <Tabs activeId={tab} onChange={(id) => setTab(id as ReportTab)} variant="underline">
                            {reportTabs.map((t) => (
                                <Tabs.Tab key={t.id} id={t.id} label={t.label} />
                            ))}
                        </Tabs>

                        {tab === 'overview' && <Overview report={report} now={now} />}
                        {tab === 'slow' && <SlowRequests environmentName={environmentName} />}
                        {tab === 'monitoring' && <Monitoring environmentName={environmentName} first={report.metrics} />}
                        {tab === 'logs' && (
                            <Logs environmentName={environmentName} consoleUrl={report.links.logs} now={now} />
                        )}
                        {tab === 'events' && <Events report={report} />}

                        <div className={theme.links}>
                            <span className={shared.muted}>In AWS:</span>
                            <a href={report.links.health} target="_blank" rel="noreferrer">
                                Health
                            </a>
                            <a href={report.links.logs} target="_blank" rel="noreferrer">
                                Logs
                            </a>
                            <a href={report.links.monitoring} target="_blank" rel="noreferrer">
                                Monitoring
                            </a>
                            <a href={report.links.dashboard} target="_blank" rel="noreferrer">
                                Environment
                            </a>
                        </div>
                    </div>
                )}
            </Dialog.Content>
        </Dialog>
    );
};

// ---- overview: what's wrong in words, then the instances ----

const Overview: React.FC<{ report: AwsEnvironmentReport; now: number }> = ({ report, now }) => {
    const findings = diagnose(report, now);
    return (
        <>
            <section>
                <h4 className={theme.heading}>What&apos;s going on</h4>
                {findings.length === 0 ? (
                    <p className={theme.allGood}>Nothing wrong right now, and no repeated problems in the last day.</p>
                ) : (
                    <ul className={theme.findings}>
                        {findings.map((f) => (
                            <li key={f.text} className={theme[f.tone]}>
                                {f.text}
                            </li>
                        ))}
                    </ul>
                )}
                {report.errors.map((e) => (
                    <p key={e.section} className={theme.error}>
                        {e.section}: {e.message}
                    </p>
                ))}
            </section>

            <section>
                <h4 className={theme.heading}>Instances</h4>
                <div className={theme.tableWrap}>
                    <table className={shared.table}>
                        <thead>
                            <tr>
                                <th>Instance</th>
                                <th>Health</th>
                                <th className={shared.num}>CPU</th>
                                <th className={shared.num}>Req / min</th>
                                <th className={shared.num}>5xx / min</th>
                                <th className={shared.num}>p90</th>
                                <th>Running since</th>
                                <th>Deploy</th>
                            </tr>
                        </thead>
                        <tbody>
                            {report.instances.map((i) => (
                                <tr key={i.id}>
                                    <td>
                                        <a href={report.links.ec2Base + i.id} target="_blank" rel="noreferrer">
                                            {i.id}
                                        </a>
                                        <div className={shared.muted}>
                                            {i.instanceType} · {i.availabilityZone}
                                        </div>
                                    </td>
                                    <td>
                                        <span className={`${shared.dot} ${shared[i.health]}`} /> {i.healthStatus}
                                        {i.causes.map((c) => (
                                            <div key={c} className={theme.cause}>
                                                {c}
                                            </div>
                                        ))}
                                    </td>
                                    <td className={shared.num}>{orDash(i.cpuPercent, (v) => `${Math.round(v)}%`)}</td>
                                    <td className={shared.num}>{orDash(i.requestsPerMinute, formatCount)}</td>
                                    <td className={`${shared.num} ${i.status5xxPerMinute ? theme.bad : ''}`}>
                                        {orDash(i.status5xxPerMinute, formatCount)}
                                    </td>
                                    <td className={shared.num}>{orDash(i.p90Ms, formatMs)}</td>
                                    <td className={shared.muted}>{i.launchedAt ? timeAgo(i.launchedAt, now) : '—'}</td>
                                    <td className={i.deploymentStatus === 'Failed' ? theme.bad : undefined}>
                                        {i.deploymentStatus ?? '—'}
                                        {i.deployedAt && <div className={shared.muted}>{timeAgo(i.deployedAt, now)}</div>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>
        </>
    );
};

// ---- slow requests: Sentry's tracing, per endpoint ----

const SlowRequests: React.FC<{ environmentName: string }> = ({ environmentName }) => {
    const [result, setResult] = useState<{ data: { link: string; requests: SlowRequest[] } | null; error: string | null }>();
    useEffect(() => {
        setResult(undefined);
        getSlowRequests(environmentName).then(setResult);
    }, [environmentName]);

    if (!result) return <p className={shared.muted}>Asking Sentry…</p>;
    if (result.error || !result.data) return <p className={theme.error}>{result.error}</p>;
    const { requests, link } = result.data;
    return (
        <section>
            <h4 className={theme.heading}>Slowest endpoints · last hour</h4>
            {requests.length === 0 ? (
                <p className={shared.muted}>
                    No request timings from this service in the last hour. It may not send traces to Sentry; that&apos;s a
                    TracesSampleRate setting in the service.
                </p>
            ) : (
                <div className={theme.tableWrap}>
                    <table className={shared.table}>
                        <thead>
                            <tr>
                                <th>Endpoint</th>
                                <th className={shared.num}>p95</th>
                                <th className={shared.num}>p50</th>
                                <th className={shared.num}>Requests</th>
                                <th className={shared.num}>Failed</th>
                            </tr>
                        </thead>
                        <tbody>
                            {requests.map((r) => (
                                <tr key={r.name}>
                                    <td className={theme.endpoint}>
                                        <a
                                            href={r.link}
                                            target="_blank"
                                            rel="noreferrer"
                                            title="Example slow requests in Sentry">
                                            {r.name}
                                        </a>
                                    </td>
                                    <td className={`${shared.num} ${(r.p95Ms ?? 0) > SLOW_MS ? theme.bad : ''}`}>
                                        {orDash(r.p95Ms, formatDuration)}
                                    </td>
                                    <td className={shared.num}>{orDash(r.p50Ms, formatDuration)}</td>
                                    <td className={shared.num}>{formatCount(r.count)}</td>
                                    <td className={`${shared.num} ${r.failureRate ? theme.bad : ''}`}>
                                        {orDash(r.failureRate, (v) => `${Math.round(v * 100)}%`)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            <p className={`${shared.muted} ${theme.note}`}>
                Sentry only times a sample of requests. Click an endpoint for example slow requests and where the time went
                (database, HTTP calls).{' '}
                <a href={link} target="_blank" rel="noreferrer">
                    All in Sentry
                </a>
            </p>
        </section>
    );
};

const formatDuration = (ms: number) =>
    ms >= 10_000 ? `${(ms / 1000).toFixed(0)} s` : ms >= 1000 ? `${(ms / 1000).toFixed(1)} s` : `${Math.round(ms)} ms`;

// ---- monitoring: the environment's own load balancer and CPU, over a chosen range ----

const Monitoring: React.FC<{ environmentName: string; first: AwsReportMetrics | null }> = ({ environmentName, first }) => {
    const [range, setRange] = useState<MetricRange>('30m');
    const [metrics, setMetrics] = useState<AwsReportMetrics | null>(first);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setError(null);
        if (range === '30m' && first) {
            setMetrics(first);
            return;
        }
        let ignore = false; // a slower answer for the previous range mustn't land on this one
        setMetrics(null);
        getAwsReportMetrics(environmentName, range)
            .then((m) => !ignore && setMetrics(m))
            .catch(async (e) => {
                const message = await errorText(e);
                if (!ignore) setError(message);
            });
        return () => {
            ignore = true;
        };
    }, [environmentName, range, first]);

    return (
        <section>
            <div className={shared.filters} role="group" aria-label="Time range">
                {ranges.map((r) => (
                    <button
                        key={r}
                        type="button"
                        className={shared.chip}
                        aria-pressed={range === r}
                        onClick={() => setRange(r)}>
                        {r}
                    </button>
                ))}
            </div>
            {error && <p className={theme.error}>{error}</p>}
            {!metrics && !error && <p className={shared.muted}>Loading…</p>}
            {metrics && (
                <>
                    <div className={shared.stats}>
                        {metrics.hasLoadBalancer && (
                            <>
                                <Stat label="Requests / min" history={metrics.requestsPerMinute} format={formatCount} />
                                <Stat
                                    label="5xx rate"
                                    history={metrics.errorRate}
                                    format={formatPercent}
                                    isBad={(v) => v > 1}
                                />
                                <Stat label="p95 latency" history={metrics.p95Ms} format={formatMs} isBad={(v) => v > 500} />
                            </>
                        )}
                        <Stat
                            label="CPU"
                            history={metrics.cpuPercent}
                            format={(v) => `${Math.round(v)}%`}
                            isBad={(v) => v > 80}
                        />
                    </div>
                    <p className={`${shared.muted} ${theme.note}`}>
                        {metrics.timestamps.length > 0 &&
                            `${clock(metrics.timestamps[0])} to ${clock(metrics.timestamps[metrics.timestamps.length - 1])}. `}
                        Each tile shows the latest value; the line is the whole range.
                        {!metrics.hasLoadBalancer &&
                            ' This environment has no load balancer of its own, so only CPU is shown.'}
                    </p>
                </>
            )}
        </section>
    );
};

// ---- logs: EB's tail of every log file on every instance ----

const Logs: React.FC<{ environmentName: string; consoleUrl: string; now: number }> = ({
    environmentName,
    consoleUrl,
    now,
}) => {
    const [logs, setLogs] = useState<AwsTailLogs | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [filter, setFilter] = useState('');
    const [errorsOnly, setErrorsOnly] = useState(false);

    const fetchLogs = () => {
        setLoading(true);
        setError(null);
        fetchAwsLogs(environmentName)
            .then((l) => {
                setLogs(l);
                setError(l.error);
            })
            .catch(async (e) => setError(await errorText(e)))
            .finally(() => setLoading(false));
    };

    const matches = (line: string) =>
        (!errorsOnly || ERROR_LINE.test(line)) && (!filter || line.toLowerCase().includes(filter.toLowerCase()));

    return (
        <section>
            <div className={theme.logToolbar}>
                <Button size="sm" variant="tertiary" icon={faFileLines} onClick={fetchLogs} disabled={loading}>
                    {loading ? 'Collecting logs…' : logs ? 'Fetch again' : 'Fetch latest logs'}
                </Button>
                {logs && (
                    <>
                        <input
                            className={theme.logFilter}
                            type="search"
                            placeholder="Filter lines"
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                        />
                        <label className={theme.logCheck}>
                            <input type="checkbox" checked={errorsOnly} onChange={(e) => setErrorsOnly(e.target.checked)} />{' '}
                            Errors only
                        </label>
                    </>
                )}
            </div>
            {!logs && !loading && !error && (
                <p className={shared.muted}>
                    Asks each instance for the last lines of every log file, like the Logs button in AWS. Takes 10–40
                    seconds.
                </p>
            )}
            {error && (
                <p className={theme.error}>
                    {error}{' '}
                    <a href={consoleUrl} target="_blank" rel="noreferrer">
                        Open the logs in the AWS console instead
                    </a>
                </p>
            )}
            {logs && !logs.error && logs.instances.length === 0 && (
                <p className={shared.muted}>AWS didn&apos;t return any logs in time. Try again.</p>
            )}
            {logs && logs.missing.length > 0 && (
                <p className={theme.warning}>
                    {logs.missing.join(', ')} didn&apos;t upload in time; fetch again to include{' '}
                    {logs.missing.length === 1 ? 'it' : 'them'}.
                </p>
            )}
            {logs?.instances.map((instance) => (
                <div key={instance.instanceId} className={theme.logInstance}>
                    <h4 className={theme.heading}>
                        {instance.instanceId}
                        {instance.at && <span className={shared.muted}> · collected {timeAgo(instance.at, now)}</span>}
                    </h4>
                    {instance.error && <p className={theme.error}>{instance.error}</p>}
                    {instance.files.map((file) => {
                        const lines = file.lines.filter(matches);
                        if ((filter || errorsOnly) && lines.length === 0) return null;
                        return (
                            <details
                                key={file.name}
                                className={theme.logFile}
                                open={file.lines.some((l) => ERROR_LINE.test(l))}>
                                <summary>
                                    {file.name} <span className={shared.muted}>· {lines.length} lines</span>
                                </summary>
                                <pre className={theme.logLines}>
                                    {lines.map((line, n) => (
                                        <div key={n} className={ERROR_LINE.test(line) ? theme.logError : undefined}>
                                            {line}
                                        </div>
                                    ))}
                                </pre>
                            </details>
                        );
                    })}
                </div>
            ))}
        </section>
    );
};

// ---- events: EB's own log of deploys, restarts and health changes ----

const Events: React.FC<{ report: AwsEnvironmentReport }> = ({ report }) =>
    report.events.length === 0 ? (
        <p className={shared.muted}>No events in the last 24 hours.</p>
    ) : (
        <ul className={theme.events}>
            {report.events.map((e) => (
                <li key={e.id} className={theme[e.severity]}>
                    <span className={theme.eventTime}>{clock(e.at)}</span>
                    <span className={theme.eventMessage}>{e.message}</span>
                </li>
            ))}
        </ul>
    );

export default ServiceReport;
